import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import { billingSplitQueryKey } from 'Data/keys/QueryKeys';
import { RateioApi } from 'Data/Rateio.api';
import { IBillingSplitResponse } from 'Data/interfaces/Faturamento/IRateio';
import { ISpeRequest } from 'Data/interfaces/Faturamento/ISpe';
import toastHandler from 'Utils/toastHandler';
import { getApiErrorMessage } from 'Utils/ApiErrorUtils';
import i18n from 'i18n';
import { useRateioScope } from './RateioScope';

export interface IUpdateSpeVariables {
  speId: string;
  spe: ISpeRequest;
}

// Mutations de cadastro de SPE.
//
// Diferente de useMutationRateio, que escreve o cache na mão, aqui invalidamos a
// query: incluir ou remover uma SPE reflui os pesos do rateio no servidor (em
// Uniform redistribui, em Custom deixa a soma fora de 100 — ver
// reflowAfterSpeChange no adaptador). addSpe/updateSpe devolvem só a SPE, não o
// estado do rateio, então montar o snapshot novo aqui seria adivinhar.
export const useMutationSpe = () => {
  const { tenantId } = useRateioScope();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: billingSplitQueryKey(tenantId) });
  };

  const addMutation = useMutation({
    mutationFn: (spe: ISpeRequest) => RateioApi.addSpe(spe, tenantId),
    onSuccess: () => {
      invalidate();
      toastHandler.showSuccess(i18n.t('faturamento:spe.addSuccess'));
    },
    onError: (error: unknown) => {
      toastHandler.showError(getApiErrorMessage(error, i18n.t('faturamento:spe.addError')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ speId, spe }: IUpdateSpeVariables) =>
      RateioApi.updateSpe(speId, spe, tenantId),
    onSuccess: () => {
      invalidate();
      toastHandler.showSuccess(i18n.t('faturamento:spe.updateSuccess'));
    },
    onError: (error: unknown) => {
      toastHandler.showError(getApiErrorMessage(error, i18n.t('faturamento:spe.updateError')));
    },
  });

  // removeSpe devolve o estado completo, então aproveitamos para já refletir o
  // rateio refluído sem esperar o refetch — e invalidamos em seguida para não
  // divergir caso o servidor tenha mudado algo mais.
  const removeMutation = useMutation({
    mutationFn: (speId: string) => RateioApi.removeSpe(speId, tenantId),
    onSuccess: (billingSplit: IBillingSplitResponse) => {
      queryClient.setQueryData<IBillingSplitResponse>(
        billingSplitQueryKey(tenantId),
        billingSplit
      );
      invalidate();
      toastHandler.showSuccess(i18n.t('faturamento:spe.removeSuccess'));
    },
    onError: (error: unknown) => {
      toastHandler.showError(getApiErrorMessage(error, i18n.t('faturamento:spe.removeError')));
    },
  });

  return {
    addSpe: addMutation.mutate,
    updateSpe: updateMutation.mutate,
    removeSpe: removeMutation.mutate,
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRemoving: removeMutation.isPending,
    isSaving: addMutation.isPending || updateMutation.isPending,
  };
};
